import React from 'react';
import PropTypes from 'prop-types';
import {
  Dialog, DialogTitle, DialogContent, DialogActions, Button,
  Chip, Stack, Box, Avatar
} from '@mui/material';
import ArrowForwardRoundedIcon from '@mui/icons-material/ArrowForwardRounded';
import PlayCircleFilledRoundedIcon from '@mui/icons-material/PlayCircleFilledRounded';
import CheckCircleRoundedIcon from '@mui/icons-material/CheckCircleRounded';
import { styled, keyframes } from '@mui/system';

const pulse = keyframes`0%{transform:scale(1);opacity:.28}50%{transform:scale(1.08);opacity:.65}100%{transform:scale(1);opacity:.28}`;
const nudge = keyframes`0%{transform:translateX(0);opacity:.8}50%{transform:translateX(4px);opacity:1}100%{transform:translateX(0);opacity:.8}`;

const PulseWrap = styled('div')(({ theme }) => ({
  position: 'relative', display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
  padding: 2, borderRadius: 999,
  '&::before': {
    content: '""', position: 'absolute', inset: -6, borderRadius: 999,
    background: theme.palette.mode === 'dark' ? 'rgba(58,142,255,0.25)' : 'rgba(25,118,210,0.18)',
    animation: `${pulse} 1.6s ease-in-out infinite`, zIndex: 0,
  },
  '& > *': { position: 'relative', zIndex: 1 },
}));
const Arrow = styled(ArrowForwardRoundedIcon)(({ theme }) => ({
  fontSize: 28, color: theme.palette.primary.main, animation: `${nudge} 1.3s ease-in-out infinite`,
}));
const GreyChip = styled(Chip)(({ theme }) => ({
  color: theme.palette.text.disabled,
  borderColor: theme.palette.divider,
  '& .MuiChip-icon': { color: theme.palette.text.disabled },
}));

const ProceedMappingDialog = ({
  open,
  onClose,
  onProceed,
  // New API
  mappings,
  activeIndex = 0,
  mode = 'start',
  source = '',
  destination1 = '',
  destination2 = '',
  title,
  actionText = 'Okay',
}) => {
  const fallbackMappings = [
    { sourceLabel: source || '', destinationLabel: destination1 || '' },
    { sourceLabel: source || '', destinationLabel: destination2 || '' },
  ].filter(m => m.destinationLabel);

  const list = Array.isArray(mappings) && mappings.length ? mappings : fallbackMappings;

  const computeState = (i) => {
    if (!mappings || !mappings.length) {
      if (mode === 'start') return i === 0 ? 'active' : 'pending';
      return i === 0 ? 'done' : 'active';
    }
    if (i < activeIndex) return 'done';
    if (i === activeIndex) return 'active';
    return 'pending';
  };

  const resolveLabels = (m, i) => ({
   src: m.sourceLabel ?? m.source?.label ?? m.sourceName ?? m.source ?? 'Source',
    dst: m.destinationLabel ?? m.destination?.label ?? m.destinationName ?? m.destination ?? `Mapping ${i + 1}`,
  });

  const chipFor = (m, i) => {
    const state = computeState(i);
    // const label = (
     const { src, dst } = resolveLabels(m, i);
     const label = (
      <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5, maxWidth: 280 }}
         title={`${src} → ${dst}`}>
        <Avatar sx={{ width: 18, height: 18 }} />
        <Box sx={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
          {/* {(m.sourceLabel || 'Source')} &nbsp;→&nbsp; {(m.destinationLabel || `Mapping ${i + 1}`)} */}
          {src} &nbsp;→&nbsp; {dst}
        </Box>
      </Box>
    );
    if (state === 'active') {
      return (
        <PulseWrap key={i}>
          <Chip icon={<PlayCircleFilledRoundedIcon />} label={label} />
        </PulseWrap>
      );
    }
    if (state === 'done') {
      return (
        <GreyChip key={i} icon={<CheckCircleRoundedIcon />} variant="outlined" label={label} />
      );
    }
    return <GreyChip key={i} variant="outlined" label={label} />;
  };

  const safeTitle =
    title || `Proceeding to Mapping ${Math.min(activeIndex + 1, list.length)}`;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="xs"
      fullWidth
      PaperProps={{
        sx: (theme) => ({
          border: `1px solid ${theme.palette.primary.light}`,
          boxShadow: `0 6px 24px rgba(25,118,210,0.25)`,
        }),
      }}
    >
      <DialogTitle sx={{ color: '#4C5B9A', fontWeight: 600, pb: 2, fontSize: '1rem' }}>
        {safeTitle}
      </DialogTitle>

      {/* <DialogContent sx={{ py: 2.5 }}>
        <Stack direction="row" alignItems="center" justifyContent="center" spacing={1.25}>
          <Arrow />
          {list[activeIndex] ? chipFor(list[activeIndex], activeIndex) : null}
        </Stack>

        <Box mt={2}>
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="center"
            spacing={1}
            useFlexGap
            flexWrap="wrap"
          >
            {list.map((m, i) => i !== activeIndex ? chipFor(m, i) : null)}
          </Stack>
        </Box>
      </DialogContent> */}

      <DialogContent sx={{ py: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center',padding: 4}}>
        <Stack direction="row" alignItems="center" justifyContent="center" spacing={2.5} useFlexGap flexWrap="wrap">
          {list.map((m, i) => (
            <Box key={i} sx={{ display: 'inline-flex', alignItems: 'center', gap: 1, flexWrap: 'nowrap' }}>
              {i === activeIndex && <Arrow sx={{ flex: '0 0 auto', color: '#4C5B9A' }} />}
              {chipFor(m, i)}
            </Box>
          ))}
        </Stack>
        </Box>
     </DialogContent>

      <DialogActions sx={{ px: 2, pb: 2 }}>
        <Button variant="contained" onClick={() => (onProceed ? onProceed(activeIndex) : onClose())} sx={{backgroundColor: '#5965A6',textTransform: 'none', fontWeight: 500, '&:hover': {backgroundColor: '#4C5B9A'}}}>
          {actionText}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

ProceedMappingDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onProceed: PropTypes.func,
  mappings: PropTypes.arrayOf(PropTypes.shape({
    sourceLabel: PropTypes.string,
    destinationLabel: PropTypes.string,
  })),
  activeIndex: PropTypes.number,
  mode: PropTypes.oneOf(['start', 'next']),
  source: PropTypes.string,
  destination1: PropTypes.string,
  destination2: PropTypes.string,
  title: PropTypes.string,
  actionText: PropTypes.string,
};

export default ProceedMappingDialog;
